import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { checkResetController, resetController } from "./auth.controller";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
	Form,
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormMessage,
	FormDescription,
} from "@/components/ui/form";
import { toaster } from "@/components/Toaster";
import { Link, useNavigate } from "react-router-dom";
import useGcpsUIController from "@/hooks/useGlobalContext";
import { useEffect, useState } from "react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { FieldDescription } from "@/components/ui/field";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { cn, handleMutationError, withPrefix } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { routes } from "@/app/routes/paths";

const resetSchema = z.object({
	username: z.string().min(1, "Resident ID is required")
});

const checkResetSchema = z.object({
	username: z.string().min(1, "Resident ID is required"),
	password: z.string()
		.min(8, "Password must be at least 8 characters")
		.regex(/[A-Z]/, "Password must contain at least one uppercase letter")
		.regex(/[a-z]/, "Password must contain at least one lowercase letter")
		.regex(/[0-9]/, "Password must contain at least one number")
		.regex(/[@$!%*?&#^()\-_=+{}[\]|\\:;"'<>,.?/]/,
			"Password must contain at least one special character"),
	password_confirmation: z.string().min(8, "Password must be at least 6 characters"),
	sms_code: z.string().min(6, "SMS code must be at least 6 characters"),
})
	.superRefine((data, ctx) => {
		// Confirm password match
		if (data.password !== data.password_confirmation) {
			ctx.addIssue({
				path: ["password_confirmation"],
				message: "Passwords do not match",
				code: z.ZodIssueCode.custom,
			});
		}
	});


type FormValues = z.infer<typeof resetSchema>;
type CheckFormValues = z.infer<typeof checkResetSchema>;

export const PasswordResetPage = () => {
	const { controller } = useGcpsUIController();
	const { pageTitle } = controller;
	const [step, setStep] = useState<"login" | "2fa">("login");
	const [otpMessage, setOtpMessage] = useState<string | null>(null);
	const { theme } = useTheme();

	const getColorType = () => {
		if (theme === "system") {
			return window.matchMedia("(prefers-color-scheme: dark)").matches
				? "dark"
				: "light";
		}
		return theme;
	};

	const [colorType, setColorType] = useState(getColorType);

	const navigate = useNavigate();

	const form = useForm<FormValues>({
		resolver: zodResolver(resetSchema),
		defaultValues: { username: "" },
	});

	const checkLoginForm = useForm<CheckFormValues>({
		resolver: zodResolver(checkResetSchema),
		defaultValues: { username: form.getValues("username"), password: "", password_confirmation: "", sms_code: "" },
	});

	const passwordResetMutation = useMutation({
		mutationFn: resetController,
		onError: handleMutationError,
		onSuccess: (data) => {
			setStep("2fa");
			setOtpMessage(data.message);
			checkLoginForm.setValue('username', form.getValues("username"));
		},
	});

	const resetPasswordMutation = useMutation({
		mutationFn: checkResetController,
		onError: handleMutationError,
		onSuccess: (data) => {
			navigate(routes.LOGIN_PATH);
			toaster("success", data.message || "Password reset successfully");
		},
	});

	// const { mutate } = mutation;
	const isPending = passwordResetMutation.status === "pending" || resetPasswordMutation.status === "pending";

	const onSubmit = (data: FormValues) => {
		// setCredentials({ username: data.username, password: data.password });
		passwordResetMutation.mutate({ username: data.username });
	};

	const onSubmitCheckForm = (data: CheckFormValues) => {
		resetPasswordMutation.mutate({ username: data.username, password: data.password, password_confirmation: data.password_confirmation, sms_code: data.sms_code });
	};

	useEffect(() => {
		setColorType(getColorType());
	}, [theme]);

	useEffect(() => {
		document.title = pageTitle ? `Login | ${pageTitle}` : "Resident Portal";
	}, [pageTitle]);

	return (
		<div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
			<div className="hidden md:flex items-center justify-center p-8 bg-cover bg-right relative" style={{ backgroundImage: `url(${withPrefix("assets/images/login.jpg")})` }}>
				<div className={cn("absolute inset-0 bg-white/50", colorType === 'dark' && "bg-black/60")}></div>
				<div className="max-w-md text-center relative z-10">
					{/* <img src="" alt="Illustration" className="mx-auto object-cover" /> */}
					<h2 className={cn("mt-6 text-4xl font-semibold text-yellow-500", colorType === 'light' && "text-primary")}>Welcome back</h2>
					<p className={cn("text-primary mt-5 text-2xl", colorType === 'light' && "text-black")}>Access your resident portal to manage fees, documents and support.</p>
				</div>
			</div>

			<div className="flex items-center justify-center p-8">
				<div className="w-full max-w-lg bg-background border border-border rounded-2xl p-8 shadow-lg">
					<div className="flex items-center gap-4">
						<div className="rounded-md bg-primary/10 p-2">
							<img src={withPrefix("assets/images/favicon.png")} alt="logo" className="w-10 h-10" />
						</div>
						<div>
							<h1 className="text-2xl font-semibold">Reset Password</h1>
							<p className="text-muted-foreground text-sm mt-1">Provide the necessary information below to finish resetting your password.</p>
						</div>
					</div>
					{step === "2fa" && otpMessage && (
						<div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
							<p className="text-yellow-800" dangerouslySetInnerHTML={{ __html: otpMessage }}></p>
						</div>
					)}
					{step === "login" && (
						<Form {...form}>
							<form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-4">
								<FormField
									control={form.control}
									name="username"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<Input label="Resident ID" placeholder="012235620" type="text" {...field} />
											</FormControl>
											<FormMessage />
											<FormDescription>Please enter resident number without the slash (e.g. PS/00/00/001/M should be PS0000001M)</FormDescription>
										</FormItem>
									)}
								/>
								<div className="flex items-center justify-between">
									<Link className="text-sm text-primary hover:underline" to={routes.LOGIN_PATH}>
										Already have an account? Sign in here.
									</Link>
								</div>

								<div className="flex items-center gap-2">
									<Button type="submit" disabled={isPending} className="flex-1">
										{isPending ? "Please wait..." : "Reset Password"}
									</Button>
								</div>
							</form>
						</Form>

					)}
					{step === "2fa" && (
						<Form {...checkLoginForm}>
							<form onSubmit={checkLoginForm.handleSubmit(onSubmitCheckForm, console.log)} className="mt-6 space-y-4">
								<FormField
									control={checkLoginForm.control}
									name="sms_code"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Verification code</FormLabel>

											<FormControl>
												<InputOTP
													pattern={REGEXP_ONLY_DIGITS}
													maxLength={6}
													value={field.value}
													onChange={field.onChange}
												>
													<InputOTPGroup className="gap-2.5 *:data-[slot=input-otp-slot]:p-6 *:data-[slot=input-otp-slot]:text-[26px] *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border w-full justify-evenly">
														<InputOTPSlot index={0} />
														<InputOTPSlot index={1} />
														<InputOTPSlot index={2} />
														<InputOTPSlot index={3} />
														<InputOTPSlot index={4} />
														<InputOTPSlot index={5} />
													</InputOTPGroup>
												</InputOTP>
											</FormControl>

											<FormDescription>
												Enter the 6-digit code sent to your email.
											</FormDescription>

											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={checkLoginForm.control}
									name="password"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<Input label="New Password" placeholder="••••••••" type="password" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={checkLoginForm.control}
									name="password_confirmation"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<Input label="Confirm New Password" placeholder="••••••••" type="password" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<div className="flex flex-col gap-2">
									<Button type="submit" className="mb-5" loading={resetPasswordMutation.isPending}>Reset Password</Button>
									<FieldDescription className="text-center">
										<Link className="text-sm text-primary hover:underline" to="#" onClick={(e) => { e.preventDefault(); setStep('login') }}>
											Start Over
										</Link>
									</FieldDescription>
									<FieldDescription className="text-center">
										Didn&apos;t receive the code? <a href="#" onClick={form.handleSubmit(onSubmit)}>Resend</a>
									</FieldDescription>
								</div>
							</form>
						</Form>
					)}
				</div>
			</div>
		</div>
	);
};